import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AutomasPeritajeEntity from "../../../../../domain/entities/AutomasPeritajeEntity";
import GetCarAutomasByPlateApiImpl from "../../../../../data/repositories/automas/GetCarAutomasByPlateApiImpl";
import CertificadoPeritajeAutomas from "../../../components/certificadoPeritajeAutomas/CertificadoPeritajeAutomas";
import ModalCertificadoPeritajeAutomas from "../../../components/certificadoPeritajeAutomas/ModalCertificadoPeritajeAutomas";
import ModalPeritajeVehiculo from "../detailedCar/component/ModalPeritajeVehiculo";
import {
    automasPeritajeCarMock,
    automasPeritajeMotoMock,
} from "../../../components/certificadoPeritajeAutomas/automasPeritajeMock";
import "./PeritajeAutomasDemoPageStyles.scss";

type Source = "car" | "moto" | "api";

/**
 * Única placa con peritaje cargado en el backend por ahora. Queda quemada para poder probar
 * sin escribirla cada vez; cuando haya más peritajes basta con borrar esta constante y su uso.
 */
export const PLACA_DE_PRUEBA = "RNU052";

/**
 * Pantalla de prueba del certificado de peritaje de Automás.
 *
 * Existe solo para revisar el diseño mientras se define en qué casos reales se debe mostrar
 * el peritaje. Permite ver el mock con la respuesta actual de la API, el mock con el formato
 * completo (campos opcionales poblados) y consultar una placa real contra
 * `get-car-automas/<placa>/`.
 */
const PeritajeAutomasDemoPage: FC = () => {
    const { plate: plateParam } = useParams<{ plate?: string }>();
    // `?mock=moto` abre directamente el escenario con el formato completo, para poder compartir el enlace.
    const [searchParams] = useSearchParams();
    const mockParam = searchParams.get("mock");

    // Por defecto se abre el peritaje real de la placa de prueba; los mocks quedan como alternativa.
    const [source, setSource] = useState<Source>(mockParam === "moto" ? "moto" : mockParam === "car" ? "car" : "api");
    const [plateInput, setPlateInput] = useState(plateParam ?? PLACA_DE_PRUEBA);
    const [apiData, setApiData] = useState<AutomasPeritajeEntity | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [showCadena, setShowCadena] = useState(false);

    /**
     * `activate` decide si además se cambia la vista al peritaje real. En la carga automática va
     * en false: si no, entrar con `?mock=moto` traía el mock y acto seguido lo tapaba con la API.
     */
    const fetchPlate = useCallback(async (plate: string, activate: boolean) => {
        const normalized = plate.trim().toUpperCase();
        if (!normalized) return;

        setLoading(true);
        setError(null);
        const response = await GetCarAutomasByPlateApiImpl(normalized);
        if (response) {
            setApiData(response);
            if (activate) setSource("api");
        } else {
            setApiData(null);
            setError(`No se encontró peritaje de Automás para la placa ${normalized}.`);
        }
        setLoading(false);
    }, []);

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        fetchPlate(plateInput, true);
    };

    // Carga automática al entrar, para que la pantalla abra directo en el peritaje real.
    useEffect(() => {
        fetchPlate(plateParam ?? PLACA_DE_PRUEBA, false);
    }, [plateParam, fetchPlate]);

    const data: AutomasPeritajeEntity | null =
        source === "car" ? automasPeritajeCarMock : source === "moto" ? automasPeritajeMotoMock : apiData;

    return (
        <div className="peritaje-demo">
            <header className="peritaje-demo__bar">
                <div>
                    <h1>Certificado de peritaje Automás</h1>
                    <p>
                        Pantalla de prueba. Fuente de datos: <code>GET /get-car-automas/&lt;placa&gt;/</code>
                    </p>
                </div>

                <div className="peritaje-demo__controls">
                    <div className="peritaje-demo__tabs">
                        <button
                            type="button"
                            className={source === "car" ? "is-active" : ""}
                            onClick={() => setSource("car")}
                        >
                            Mock automóvil
                        </button>
                        <button
                            type="button"
                            className={source === "moto" ? "is-active" : ""}
                            onClick={() => setSource("moto")}
                        >
                            Mock moto (formato completo)
                        </button>
                        <button
                            type="button"
                            className={source === "api" ? "is-active" : ""}
                            onClick={() => setSource("api")}
                        >
                            Placa real
                        </button>
                    </div>

                    <form className="peritaje-demo__plate" onSubmit={onSubmit}>
                        <input
                            type="text"
                            value={plateInput}
                            placeholder="ABC123"
                            maxLength={8}
                            onChange={(event) => setPlateInput(event.target.value.toUpperCase())}
                        />
                        <button type="submit" disabled={loading || !plateInput.trim()}>
                            {loading ? "Consultando…" : "Consultar"}
                        </button>
                    </form>

                    <button type="button" className="peritaje-demo__modal-btn" onClick={() => setShowModal(true)}>
                        Ver en modal
                    </button>
                    <button type="button" className="peritaje-demo__modal-btn" onClick={() => setShowCadena(true)}>
                        Probar cadena Automás → Colserauto
                    </button>
                </div>
            </header>

            <main className="peritaje-demo__stage">
                {source === "api" && loading && <div className="peritaje-demo__msg">Consultando el peritaje…</div>}
                {source === "api" && !loading && error && (
                    <div className="peritaje-demo__msg peritaje-demo__msg--error">{error}</div>
                )}
                {source === "api" && !loading && !apiData && !error && (
                    <div className="peritaje-demo__msg">
                        Escribe una placa y presiona <b>Consultar</b> para traer el peritaje real.
                    </div>
                )}
                {data && <CertificadoPeritajeAutomas data={data} />}
            </main>

            {showCadena && (
                <ModalPeritajeVehiculo
                    plate={(plateInput || PLACA_DE_PRUEBA).trim().toUpperCase()}
                    close={() => setShowCadena(false)}
                />
            )}

            {showModal && data && <ModalCertificadoPeritajeAutomas data={data} close={() => setShowModal(false)} />}
        </div>
    );
};

export default PeritajeAutomasDemoPage;
